/**
 * reverseGeocode.js — Browser geolocation + reverse-geocoding utilities used
 * by the shared LocationSelector "Use my location" button.
 *
 * Why this file exists
 * --------------------
 * The filter sheet needs to translate raw GPS coordinates into the same
 * stable `country` / `city` slug keys the backend uses for filtering. We
 * do this in three steps:
 *   1. Browser asks for permission and returns lat/lng.
 *   2. A free, key-less reverse-geocode endpoint (BigDataCloud's
 *      `reverse-geocode-client`) maps coords → ISO-2 country code + city /
 *      locality strings.
 *   3. Pure-client mapping helpers turn the ISO code into our country slug
 *      and fuzzy-match the city name into one of our city keys (case- and
 *      diacritic-insensitive, EN + AR).
 *
 * No API keys, no backend changes, no PII storage. Country detection is
 * deterministic (ISO map). City detection is best-effort — when the
 * geocoder returns a city we don't ship in our catalogue we leave the city
 * field empty and let the caller surface a small note.
 */

const REVERSE_GEOCODE_URL = 'https://api.bigdatacloud.net/data/reverse-geocode-client';

/**
 * ISO-3166-1 alpha-2 → backend country slug. Only the entries we currently
 * surface in `/api/student-hub/countries` are mapped — every other country
 * resolves to `null` (caller keeps the country field empty).
 */
const ISO_TO_COUNTRY_KEY = {
  SD: 'sudan',
  EG: 'egypt',
  LY: 'libya',
  TN: 'tunisia',
  DZ: 'algeria',
  MA: 'morocco',
  SA: 'saudi_arabia',
  AE: 'uae',
  QA: 'qatar',
  KW: 'kuwait',
  OM: 'oman',
  BH: 'bahrain',
  JO: 'jordan',
  IQ: 'iraq',
  ET: 'ethiopia',
  KE: 'kenya',
  UG: 'uganda',
  TZ: 'tanzania',
  SO: 'somalia',
  TD: 'chad',
  ER: 'eritrea',
  SS: 'south_sudan',
  GH: 'ghana',
  GN: 'guinea',
  MR: 'mauritania',
  MZ: 'mozambique',
  ML: 'mali',
  CD: 'congo', // Democratic Republic of the Congo
  CG: 'congo', // Republic of the Congo (same `congo` slug, both capitals exposed)
  ZM: 'zambia',
};

/**
 * Promise wrapper around `navigator.geolocation.getCurrentPosition`.
 * Errors propagate with `.code` so callers can map them to localized strings.
 */
export function getCurrentPositionAsync(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const err = new Error('Geolocation is not supported on this device');
      err.code = 'UNSUPPORTED';
      reject(err);
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 5 * 60 * 1000, // 5 min cache
      ...options,
    });
  });
}

/**
 * Hit BigDataCloud's free client-side reverse-geocoder.
 *
 * IMPORTANT — always requests Arabic locality names regardless of UI
 * language. BigDataCloud's English output uses transliterations that
 * diverge from our `name_en` slugs (e.g. it returns `Gedaref` while our
 * catalogue keeps `Gedarif`; `Madani` vs `Wad Madani`; `Al Ubayyid` vs
 * `El Obeid`), which silently broke EN-mode city auto-fill. The Arabic
 * names match our `name_ar` field byte-for-byte, so we always geocode
 * in Arabic and rely on `matchCityByName` to fuzzy-resolve the slug.
 * The display label is then rendered by the dropdown in the user's UI
 * language from the same canonical `cityKey` — no AR/EN drift in state.
 */
export async function reverseGeocode(latitude, longitude, _language = 'ar') {
  const url = `${REVERSE_GEOCODE_URL}?latitude=${encodeURIComponent(latitude)}&longitude=${encodeURIComponent(longitude)}&localityLanguage=ar`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Reverse-geocode failed: ${res.status}`);
  const data = await res.json();
  return {
    countryCode: (data.countryCode || '').toUpperCase(),
    city: data.city || '',
    locality: data.locality || '',
    principalSubdivision: data.principalSubdivision || '',
  };
}

/**
 * Coarse country bounding-box fallback. Used only if the network reverse-
 * geocoder fails (rare — banned IPs, offline, ad-blockers). Boxes are
 * intentionally generous so a tap near a border still resolves; conflicts
 * are resolved by checking smaller boxes first. Cities are not detectable
 * from a bbox so callers will still keep the city field empty.
 */
const COUNTRY_BBOXES = [
  // [countryKey,  minLat, maxLat, minLng, maxLng]
  ['sudan',        8.7, 22.3, 21.8, 38.7],
  ['south_sudan',  3.5,  12.3, 23.4, 35.9],
  ['egypt',        22.0, 31.7, 24.7, 36.9],
  ['libya',        19.5, 33.2, 9.4,  25.2],
  ['tunisia',      30.2, 37.6, 7.5,  11.6],
  ['algeria',      18.9, 37.1, -8.7, 12.0],
  ['morocco',      21.4, 35.9, -17.1, -1.0],
  ['saudi_arabia', 16.3, 32.2, 34.5, 55.7],
  ['uae',          22.6, 26.1, 51.5, 56.4],
  ['qatar',        24.4, 26.2, 50.7, 51.7],
  ['kuwait',       28.5, 30.1, 46.5, 48.5],
  ['oman',         16.6, 26.4, 51.9, 60.0],
  ['bahrain',      25.5, 26.3, 50.3, 50.9],
  ['jordan',       29.1, 33.4, 34.9, 39.4],
  ['iraq',         29.0, 37.5, 38.7, 48.7],
  ['ethiopia',     3.4,  14.9, 32.9, 48.0],
  ['kenya',        -4.7,  5.1, 33.9, 41.9],
  ['uganda',       -1.5,  4.3, 29.5, 35.1],
  ['tanzania',     -11.8, -0.9, 29.3, 40.5],
  ['somalia',      -1.7, 12.0, 40.9, 51.5],
  ['chad',         7.4,  23.5, 13.5, 24.0],
  ['eritrea',      12.4, 18.0, 36.5, 43.1],
  ['ghana',        4.7, 11.2, -3.3, 1.2],
  ['guinea',       7.2, 12.7, -15.1, -7.6],
  ['mauritania',   14.7, 27.3, -17.1, -4.8],
  ['mozambique',   -26.9, -10.5, 30.2, 40.9],
  ['mali',         10.1, 25.0, -12.3, 4.3],
  ['congo',        -13.5,  5.4, 11.1, 31.5], // covers both DRC and RoC
  ['zambia',       -18.1, -8.2, 21.9, 33.7],
];

function matchCountryByCoords(latitude, longitude) {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return null;
  for (const [key, minLat, maxLat, minLng, maxLng] of COUNTRY_BBOXES) {
    if (latitude >= minLat && latitude <= maxLat && longitude >= minLng && longitude <= maxLng) {
      return key;
    }
  }
  return null;
}

/** Map an ISO-3166-1 alpha-2 code to our internal country slug. */
export function matchCountryByCode(isoCode) {
  if (!isoCode || typeof isoCode !== 'string') return null;
  return ISO_TO_COUNTRY_KEY[isoCode.toUpperCase()] || null;
}

/** Lowercase + strip Arabic diacritics + collapse whitespace for fuzzy match. */
function normalizeCityString(s) {
  if (!s || typeof s !== 'string') return '';
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u064B-\u065F\u0670]/g, '') // Arabic harakat
    .replace(/[\u0300-\u036f]/g, '')       // Latin diacritics
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Best-effort fuzzy match from a free-text city/locality string into one of
 * our `city.key` values for the given country. Tries the geocoder's `city`,
 * `locality`, and `principalSubdivision` fields in order — each candidate
 * is tested against BOTH `name_en` and `name_ar` so the same logic resolves
 * regardless of which language the geocoder responded in. Returns `null`
 * when nothing reasonable matches — caller surfaces a "city not detected"
 * note.
 *
 * @param {string} cityName       BigDataCloud `city` field
 * @param {string} locality       BigDataCloud `locality` field
 * @param {Array<{key,name_en,name_ar}>} citiesList from /api/student-hub/countries
 * @param {string} [principalSubdivision] BigDataCloud `principalSubdivision`
 */
export function matchCityByName(cityName, locality, citiesList, principalSubdivision) {
  if (!Array.isArray(citiesList) || citiesList.length === 0) return null;
  const candidates = [cityName, locality, principalSubdivision]
    .map(normalizeCityString)
    .filter(Boolean);
  if (candidates.length === 0) return null;

  for (const candidate of candidates) {
    // Exact normalized match against EN or AR
    const exact = citiesList.find((c) => {
      const en = normalizeCityString(c.name_en);
      const ar = normalizeCityString(c.name_ar);
      return en === candidate || ar === candidate;
    });
    if (exact) return exact.key;

    // Substring containment either direction (handles "Cairo Governorate" → "cairo",
    // and our short keys appearing in longer geocoder strings).
    const partial = citiesList.find((c) => {
      const en = normalizeCityString(c.name_en);
      const ar = normalizeCityString(c.name_ar);
      if (!en && !ar) return false;
      return (
        (en && (candidate.includes(en) || en.includes(candidate))) ||
        (ar && (candidate.includes(ar) || ar.includes(candidate)))
      );
    });
    if (partial) return partial.key;
  }

  return null;
}

/**
 * One-shot helper used by the LocationSelector button. Asks for permission,
 * reverse-geocodes, returns `{ coords, countryKey, cityKey, rawCity }`.
 *
 * Throws on permission denied / unsupported / unavailable / timeout — caller
 * maps `.code` (1/2/3 from the geolocation API or 'UNSUPPORTED'/'GEOCODE')
 * to a localized message.
 */
export async function detectUserLocation({ countriesCatalogue, language = 'en' } = {}) {
  const position = await getCurrentPositionAsync();
  const coords = {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
  };

  let geo = null;
  try {
    geo = await reverseGeocode(coords.latitude, coords.longitude, language);
  } catch (_e) {
    // Network reverse-geocode failed (banned IP, offline, blocker). We'll
    // fall back to the bounding-box country match below — city stays empty.
    geo = null;
  }

  let countryKey = geo ? matchCountryByCode(geo.countryCode) : null;
  if (!countryKey) {
    countryKey = matchCountryByCoords(coords.latitude, coords.longitude);
  }

  let cityKey = null;
  if (geo && countryKey && Array.isArray(countriesCatalogue)) {
    const country = countriesCatalogue.find((c) => c.key === countryKey);
    if (country) {
      cityKey = matchCityByName(
        geo.city,
        geo.locality,
        country.cities || [],
        geo.principalSubdivision,
      );
    }
  }

  return {
    coords,
    countryKey,
    cityKey,
    rawCity: geo ? (geo.city || geo.locality || '') : '',
    countryCode: geo ? geo.countryCode : '',
    geocodeFailed: !geo,
  };
}
